/**
 * Minimal type surface for the TRX PayPage Fields JS library
 * (`paypage-fields.trxservices.{net|com}/bundle.js`) — the ACH subset the
 * shared `<TrxAchFields>` component consumes, extended with the card-field
 * surface for the shared `<TrxCardFields>` component.
 *
 * The library attaches `PayPageJS` to `window` after the script loads;
 * browsers don't ship types for it, so we declare exactly the surface we
 * use — every member here is documented in §11 of
 * `docs/TRX_API_REFERENCE.md`. It is intentionally narrow: only the ACH,
 * card-field and Apple/Google Pay surfaces that the shared
 * `@order-up/common` components need. `apps/checkout/src/types/trxPayPage.ts`
 * declares the same library for its OWN input component (which mints its own
 * session, a thing this package never does) and carries the rest.
 */

/** Server-minted session triple `PayPageJS.initialize` consumes (§11.2). */
export interface TrxAchSessionData {
  readonly clientId: string;
  readonly payPageId: string;
  readonly sessionId: string;
}

/** Per-field config passed in `fields` to `PayPageJS.initialize`. */
export interface TrxPayPageFieldConfig {
  /** DOM element id where the per-field iframe mounts. */
  readonly containerId: string;
  /** Stylesheet for the iframe interior — the only in-frame styling knob. */
  readonly stylesCSS?: string;
  /** Wallet field only (`apple-pay` / `google-pay`) — the sheet's outcome. */
  readonly onPaymentResult?: (result: TrxPayPageWalletResult) => void;
  /** Wallet field only — the library's own button styling knob. */
  readonly buttonConfig?: Record<string, unknown>;
}

/**
 * Per §11.7: the Apple/Google Pay sheet's outcome.
 *
 * `CANCELLED` is the customer dismissing the sheet and is NOT a failure — a
 * reader that treats any non-`SUCCESS` status as an error tells a guest their
 * payment broke when they simply changed their mind.
 *
 * THE CRYPTOGRAM IN `data.cardSecurity` HAS A ~1-MINUTE TTL. Whatever receives
 * this must submit the charge immediately; it must never be staged behind
 * another confirmation step.
 */
export interface TrxPayPageWalletResult {
  readonly status: "SUCCESS" | "FAILED" | "CANCELLED";
  readonly message?: string;
  readonly data?: {
    readonly keyId: string;
    readonly data: string;
    readonly bin: string;
    readonly cardSecurity: {
      readonly dsrpCryptogram: string | null;
      readonly cavv: string | null;
    } | null;
  };
}

/**
 * Per §11.9: error taxonomy from `result.errors[]` and `instance.errors`.
 *
 * TWO SPELLINGS, AND THE DOCUMENTED ONE IS NOT THE ONE YOU GET. §11.9 names
 * `type` / `message`; the SHIPPED bundle emits `errorType` / `errorText`:
 *
 *   {errorType: "wrong-field", errorText: "Card number is invalid"}
 *
 * `apps/checkout` learned this in production — reading `type` alone sent every
 * real error to the `default` arm, so a permissions-error surfaced as the
 * generic "check your details" copy. `type` is therefore OPTIONAL, and nothing
 * should reach into either field directly: use {@link trxErrorType} /
 * {@link trxErrorMessage}, which read both.
 */
export interface TrxPayPageError {
  readonly type?:
    | "wrong-config"
    | "wrong-field"
    | "permissions-error"
    | "server-error"
    | "container-error"
    | "iframe-error"
    | "styles-error"
    | string;
  readonly message?: string;
  readonly field?: string;
  /** Live-bundle spelling of {@link TrxPayPageError.type}. */
  readonly errorType?: string;
  /** Live-bundle spelling of {@link TrxPayPageError.message}. */
  readonly errorText?: string;
  /** Live-bundle spelling of {@link TrxPayPageError.field}. */
  readonly fieldType?: string;
}

/** The error's taxonomy value, whichever spelling the bundle used. */
export const trxErrorType = (err: TrxPayPageError): string =>
  err.type ?? err.errorType ?? "";

/** The error's human text, whichever spelling the bundle used. */
export const trxErrorMessage = (err: TrxPayPageError): string =>
  err.message ?? err.errorText ?? "";

/**
 * The field the error names, whichever spelling the bundle used. The shipped
 * bundle writes `fieldType` (`{errorType, fieldType, errorText}`); §11.9 says
 * `field`.
 */
export const trxErrorField = (err: TrxPayPageError): string | undefined =>
  err.field ?? err.fieldType;

/** Per §11.6: ACH encryption result. */
export interface TrxPayPageAchEncryptedResult {
  readonly isSuccess: boolean;
  readonly errors?: readonly TrxPayPageError[];
  readonly Encrypted?: {
    readonly Data: string;
    readonly KeyId: string;
    readonly RoutingNumber?: string;
    readonly AccountNumberLastFour?: string;
  };
}

/** Per §11.8: card encryption result from `getEncryptedCardData()`. */
export interface TrxPayPageCardEncryptedResult {
  readonly isSuccess: boolean;
  readonly errors?: readonly TrxPayPageError[];
  readonly Encrypted?: {
    readonly Data: string;
    readonly KeyId: string;
    /** BIN (first 4–6 digits) when the session was initialized with `includeBIN: true`. */
    readonly BIN?: string;
  };
}

/**
 * Returned instance from `PayPageJS.initialize(...)` — ACH + card surface.
 * The instance always exposes both method families; the applicable ones
 * depend on which `fields` were configured at init time.
 */
export interface TrxPayPageInstance {
  // ─── ACH ──────────────────────────────────────────────────────────────
  /** Get encrypted ACH payload + (optional) routing/last-four. Deactivates fields. */
  getEncryptedAchData(): Promise<TrxPayPageAchEncryptedResult>;
  /** Re-activate ACH fields after a failed extract for retry. */
  enableAchDataFields(): void;
  // ─── Card ──────────────────────────────────────────────────────────────
  /** Get encrypted card payload. Deactivates fields on call per §11.6. */
  getEncryptedCardData(): Promise<TrxPayPageCardEncryptedResult>;
  /** Re-activate card fields after a failed `getEncryptedCardData()` call. */
  enableCardDataFields(): void;
  /** Live card-brand label when `onCardTypeChange` was wired and the user has typed enough digits. */
  readonly cardType?: string;
  // ─── Common ────────────────────────────────────────────────────────────
  /** Tear down all iframes + listeners. Call on unmount. */
  destroy(): void;
  /** Init-time errors. */
  readonly errors?: readonly TrxPayPageError[];
}

export interface TrxPayPageInitOptions {
  readonly sessionData: TrxAchSessionData;
  // ─── ACH options ────────────────────────────────────────────────────────
  /** Return routing + DDA-last-four on `getEncryptedAchData()` (ACH only). */
  readonly includeACHInfo?: boolean;
  // ─── Card options ────────────────────────────────────────────────────────
  /**
   * When `true`, `getEncryptedCardData()` result carries `Encrypted.BIN`
   * (first 4–6 digits). Safe per PCI because BIN is not sensitive.
   */
  readonly includeBIN?: boolean;
  /**
   * Fires on every card-number change with the detected brand label (e.g.
   * `"visa"`, `"mastercard"`, `"amex"`, `"discover"`).
   *
   * REQUIRED on EVERY initialize, bank-only fields included. TRX's bundle
   * checks it unconditionally (`"function"==typeof e ? keep : addWrong
   * CardTypeCallbackErr()`), so leaving it out puts "Set a valid
   * 'onCardTypeChange' callback function" in `instance.errors` and the ACH
   * form reads as failed to load — observed on UAT 2026-10-13. Bank fields
   * pass {@link ignoreTrxCardType}.
   */
  readonly onCardTypeChange: (cardType: string) => void;
  // ─── Fields (ACH + card; only include what you mount) ────────────────────
  readonly fields: {
    // ACH
    readonly routing?: TrxPayPageFieldConfig;
    readonly "account-number"?: TrxPayPageFieldConfig;
    // Card — card-number MUST be the first mounted container per §11.5
    // (the library injects its hidden orchestrator iframe into it).
    readonly "card-number"?: TrxPayPageFieldConfig;
    readonly "expiration-date"?: TrxPayPageFieldConfig;
    readonly cvv?: TrxPayPageFieldConfig;
    // Wallets — card sessions only. The library mounts its OWN native button
    // into each container and clears whatever was there. Only include
    // `apple-pay` where the browser can actually present the sheet
    // (`isApplePayAvailable`): asking for it in Chrome/Firefox makes the
    // library push an "unsupported" error into `instance.errors`, which is
    // what broke the card form for every non-Safari customer once already.
    readonly "apple-pay"?: TrxPayPageFieldConfig;
    readonly "google-pay"?: TrxPayPageFieldConfig;
  };
}

/** The global the library attaches to `window` after script load. */
export interface TrxPayPageGlobal {
  initialize(options: TrxPayPageInitOptions): Promise<TrxPayPageInstance>;
}

/**
 * Read `window.PayPageJS` without augmenting the global `Window` type.
 * The checkout and management apps each already declare their own (fuller)
 * `Window.PayPageJS` augmentation; a third structurally-different global
 * declaration would collide in any app that compiles both files. A local
 * cast keeps this package augmentation-free.
 */
export function getPayPageJsGlobal(): TrxPayPageGlobal | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as unknown as { PayPageJS?: TrxPayPageGlobal }).PayPageJS;
}

/**
 * The `onCardTypeChange` for a form with no card-number field. TRX requires a
 * function on every initialize (see {@link TrxPayPageInitOptions}); a bank
 * form has no brand to react to.
 */
export function ignoreTrxCardType(_cardType: string): void {
  // Intentionally empty: no card-number field is mounted.
}
